import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  styled,
  Box,
  TextField,
  Typography,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  accordionSummaryClasses,
  IconButton,
  Modal,
  Grid,
  Paper,
} from "@mui/material";
import {
  ArrowForwardIosSharp as ArrowForwardIosSharpIcon,
  Search as SearchIcon,
  Add as AddIcon,
  EditNote as EditNoteIcon,
  DoNotDisturbOn as DoNotDisturbOnIcon,
} from "@mui/icons-material";
import ButtonBlue from "../../../shared/buttonBlue/index.js";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { GoogleAPIkey } from "../../../../../utils/googleMapAssets";
import SiteService from "../../../../../services/site.service";
import DrawerSite from "./drawerSite.js";
import DrawerSiteLocation from "./drawerSiteLocation.js";

const placesLibrary = ["places"];

const ModalStyled = styled(Modal)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const AccordionStyled = styled((props) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderRadius: "8px",
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummaryStyled = styled((props) => (
  <AccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  borderRadius: "8px",
  backgroundColor: "white",
  height: "64px",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetailsStyled = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const containerStyle = {
  width: "100%",
  height: "200px",
  borderRadius: "8px",
};

const DropSitePin = ({ selectedCompanyId, selectedCompany }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [userFilterText, setUserFilterText] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [allSite, setAllSite] = useState([]);
  const [allSiteLocations, setAllSiteLocations] = useState([]);
  const [listAllSiteGroup, setListAllSiteGroup] = useState([]);
  const [isEditSite, setIsEditSite] = useState(false);
  const [isEditSiteLocation, setIsEditSiteLocation] = useState(false);
  const [location, setLocation] = useState([]);

  const { isLoaded } = useLoadScript({
    id: "google-map-script",
    googleMapsApiKey: GoogleAPIkey,
    libraries: placesLibrary,
    language: "th",
    region: "TH",
    loading: "async",
  });

  useEffect(() => {
    const fetchData = async () => {
      const resAllSites = await SiteService.getAllSites(selectedCompanyId);
      const resAllSiteLocations = await SiteService.getAllSiteLocations(
        selectedCompanyId
      );
      if (resAllSites.data && resAllSiteLocations.data) {
        setAllSite(resAllSites.data);
        setAllSiteLocations(resAllSiteLocations.data);
      }
    };
    fetchData();
  }, []);

  const handleFilterChange = (event) => {
    setUserFilterText(event.target.value);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // สำหรับ จัดการข้อมูล Site และ Location
  useEffect(() => {
    if (allSiteLocations.length > 0) {
      setListAllSiteGroup(groupByCompany(allSiteLocations));
    }
  }, [allSiteLocations]);

  const groupByCompany = (sites) => {
    return sites.reduce((acc, site) => {
      const { idCompany } = site;
      if (!acc[idCompany]) {
        acc[idCompany] = [];
      }
      acc[idCompany].push(site);
      return acc;
    }, {});
  };

  const selectedCompanyData = listAllSiteGroup[selectedCompanyId];

  // ค้นหา Site (จาก Table ScgSite)
  const filteredSites =
    selectedCompanyData && selectedCompanyData.length > 0
      ? selectedCompanyData.filter((site) => {
          const noSiteStr =
            site.noSite !== null && site.noSite !== undefined
              ? site.noSite.toString()
              : "";
          const siteDisplay = `Site ${noSiteStr}`
            .toLowerCase()
            .replace(/\s+/g, "");

          const searchText = userFilterText.toLowerCase().replace(/\s+/g, "");

          return searchText === "site" || siteDisplay.includes(searchText);
        })
      : [];

  // ค้นหา Site (จาก Table Sites)
  const filteredSitesName =
    allSite && allSite.length > 0
      ? allSite.filter((site) => {
          const siteDisplay = `Site ${site.siteName}`
            .toLowerCase()
            .replace(/\s+/g, "");

          const searchText = userFilterText.toLowerCase().replace(/\s+/g, "");

          return searchText === "site" || siteDisplay.includes(searchText);
        })
      : [];

  const groupBySites = filteredSitesName.map((site) => ({
    siteName: site.siteName,
    idSite: site.idSite,
    locations: filteredSites.filter((loc) => loc.noSite == site.siteName),
  }));

  const [isSiteDrawerOpen, setIsSiteDrawerOpen] = useState(false);
  const [isSiteLocationDrawerOpen, setIsSiteLocationDrawerOpen] =
    useState(false);
  // สำหรับส่งข้อมูลไปที่ Component ตอนกดที่ Accordion
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [selectedSiteName, setSelectedSiteName] = useState("");

  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [selectedSiteToDelete, setSelectedSiteToDelete] = useState(null);

  const [locationName, setLocationName] = useState("");

  const toggleSiteDrawer = (open, edit, idSite, siteName) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setIsSiteDrawerOpen(open);
    setIsEditSite(edit);
    setSelectedSiteId(idSite);
    setSelectedSiteName(siteName);
    if (!open) {
      setIsEditSite(false);
      setSelectedSiteId("");
      setSelectedSiteName("");
    }
  };

  const toggleSiteLocationDrawer =
    (open, edit, siteName, idSite, location) => (event) => {
      if (
        event.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }
      setIsSiteLocationDrawerOpen(open);
      setIsEditSiteLocation(edit);
      setSelectedSiteName(siteName);
      setSelectedSiteId(idSite);
      setLocation(location);
      if (!open) {
        setIsEditSiteLocation(false);
        setSelectedSiteName("");
        setSelectedSiteId("");
        setLocation([]);
      }
    };

  // สำหรับ ลบข้อมูล Location
  const handleDeleteSiteLocation = async (idScgSite) => {
    try {
      const res = await SiteService.deleteSiteLocation(idScgSite);
      if (res.data) {
        const resAllSites = await SiteService.getAllSiteLocations(
          selectedCompanyId
        );
        setAllSiteLocations(resAllSites.data);
        enqueueSnackbar("ลบข้อมูลสำเร็จ", { variant: "success" });
      } else {
        enqueueSnackbar("ลบข้อมูลล้มเหลว", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("เกิดข้อผิดพลาดในการลบข้อมูล", { variant: "error" });
    }
  };

  const handleOpenWarningModal = (idScgSite, nameSite) => {
    setLocationName(nameSite);
    setSelectedSiteToDelete(idScgSite);
    setOpenWarningModal(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedSiteToDelete) {
      await handleDeleteSiteLocation(selectedSiteToDelete);
      setSelectedSiteToDelete(null);
    }
    setOpenWarningModal(false);
  };

  const handleCloseWarningModal = () => {
    setOpenWarningModal(false);
    setSelectedSiteToDelete(null);
  };

  return (
    <Box sx={{ width: "100%", marginTop: "24px" }}>
      <ModalStyled
        open={openWarningModal}
        onClose={handleCloseWarningModal}
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          sx={{
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Grid container>
            <Grid item xs={12} sm={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {`ยืนยันการลบ ${locationName}`}
              </Typography>
              <Typography sx={{ mb: 3 }}>
                หากคุณลบตำแหน่งนี้ ข้อมูลทั้งหมดจะหายไปและไม่สามารถกู้คืนได้
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <ButtonBlue onClick={handleConfirmDelete}>ยืนยัน</ButtonBlue>
                <ButtonBlue onClick={handleCloseWarningModal} color="error">
                  ยกเลิก
                </ButtonBlue>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </ModalStyled>
      <TextField
        fullWidth
        placeholder="ค้นหา SITE"
        variant="outlined"
        value={userFilterText}
        onChange={handleFilterChange}
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          "& fieldset": {
            border: "none",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "24px",
          marginBottom: "16px",
        }}
      >
        <Typography fontSize={24}>รายการ SITE / ตำแหน่ง</Typography>
        <ButtonBlue
          variant="contained"
          onClick={toggleSiteDrawer(true, false, "", "")}
        >
          เพิ่ม Site
        </ButtonBlue>
      </div>
      <DrawerSite
        isOpen={isSiteDrawerOpen}
        onClose={toggleSiteDrawer(false)}
        setAllSite={setAllSite}
        setAllSiteLocations={setAllSiteLocations}
        setIsSiteDrawerOpen={setIsSiteDrawerOpen}
        isEditSite={isEditSite}
        siteId={selectedSiteId}
        siteName={selectedSiteName}
        selectedCompanyId={selectedCompanyId}
        selectedCompany={selectedCompany}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {allSite &&
        allSiteLocations &&
        Object.entries(groupBySites).length > 0 ? (
          groupBySites.map(({ siteName, idSite, locations }) => (
            <AccordionStyled
              key={siteName}
              expanded={expanded === siteName}
              onChange={handleAccordionChange(siteName)}
            >
              <AccordionSummaryStyled
                aria-controls={`panel-${siteName}-content`}
                id={`panel-${siteName}-header`}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography style={{ fontWeight: 500 }}>
                    {`Site ${siteName}`}
                  </Typography>
                  <Box>
                    <IconButton
                      onClick={toggleSiteLocationDrawer(
                        true,
                        false,
                        siteName,
                        idSite,
                        []
                      )}
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      onClick={toggleSiteDrawer(true, true, idSite, siteName)}
                    >
                      <EditNoteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummaryStyled>
              <AccordionDetailsStyled>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    flexWrap: "wrap",
                    paddingInline: "20px",
                  }}
                >
                  {locations.length > 0 ? (
                    <>
                      {locations.map((loc, index) => (
                        <Box
                          key={index}
                          sx={{
                            margin: "8px",
                            width: "300px",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              marginBottom: "8px",
                              cursor: "pointer",
                              color: "primary.main",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={toggleSiteLocationDrawer(
                              true,
                              true,
                              siteName,
                              idSite,
                              loc
                            )}
                          >
                            {loc.nameSite}
                          </Typography>
                          <Box>
                            {isLoaded && (
                              <Box sx={{ position: "relative", width: "100%" }}>
                                <Box
                                  sx={{
                                    position: "absolute",
                                    marginTop: "-20px",
                                    marginRight: "-20px",
                                    right: 0,
                                    zIndex: 1300,
                                  }}
                                >
                                  <IconButton
                                    onClick={() =>
                                      handleOpenWarningModal(
                                        loc.idScgSite,
                                        loc.nameSite
                                      )
                                    }
                                  >
                                    <DoNotDisturbOnIcon
                                      sx={{ color: "#F44336" }}
                                    />
                                  </IconButton>
                                </Box>
                                <GoogleMap
                                  mapContainerStyle={containerStyle}
                                  center={{
                                    lat: parseFloat(loc.Lat),
                                    lng: parseFloat(loc.Long),
                                  }}
                                  zoom={15}
                                >
                                  <Marker
                                    position={{
                                      lat: parseFloat(loc.Lat),
                                      lng: parseFloat(loc.Long),
                                    }}
                                  />
                                </GoogleMap>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ margin: "4px" }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                textAlign: "end",
                                color: "#9e9e9e !important",
                              }}
                            >
                              {`ปรับปรุงล่าสุด : ${new Date(
                                loc.lastUpdate
                              ).toLocaleString("th-TH", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`}
                            </Typography>
                          </Box>
                        </Box>
                      ))}{" "}
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Typography>ไม่มีรายการ</Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetailsStyled>
            </AccordionStyled>
          ))
        ) : (
          <Typography sx={{ mt: 2, textAlign: "center", fontSize: "30px" }}>
            ไม่มีรายการ
          </Typography>
        )}
      </div>
      <DrawerSiteLocation
        isOpen={isSiteLocationDrawerOpen}
        onClose={toggleSiteLocationDrawer(false)}
        // Child to Parent
        setAllSiteLocations={setAllSiteLocations}
        setIsSiteLocationDrawerOpen={setIsSiteLocationDrawerOpen}
        // Parent to Child
        siteName={selectedSiteName}
        idSite={selectedSiteId}
        loc={location}
        selectedCompanyId={selectedCompanyId}
        selectedCompany={selectedCompany}
        isLoaded={isLoaded}
        isEditSiteLocation={isEditSiteLocation}
      />
    </Box>
  );
};

export default DropSitePin;
